import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import clsx from 'clsx'
import { CircularProgress, Divider, ListSubheader, makeStyles, Menu, MenuItem } from '@material-ui/core'
import Icon from '../../atoms/Icon'
import { useFormattingContext } from '../FormattingProvider/FormattingContext'
import useCSV from '../../../hooks/useCSV'
import { useAppContext } from '../../../redux/slices/appContext'
import { flattenVisibleRows, flattenAllRows, buildHeaders, buildCSV, buildTSV } from './lib/exportRows'
import { effectiveFormat } from './lib/effectiveFormat'
import { DEFAULT_PREFERENCES } from './lib/preferencesStorage'
import { resolveDisplayName } from './lib/displayName'
import { useGroupingCatalog } from './hooks/useGroupingCatalog'
import GroupingBreadcrumb from './GroupingBreadcrumb'
import LazyExpandDialog from './LazyExpandDialog'

const DEPTH_BACKGROUNDS = [
  'var(--opal-surface-default, #fff)',
  'var(--color-neutral-50, #fafafa)',
  'var(--color-neutral-100, #f5f5f5)',
  'var(--color-neutral-150, #efefef)',
  'var(--color-neutral-200, #eaeaea)',
  'var(--color-neutral-200, #eaeaea)'
]

// Density tunes vertical compactness. Padding/height/font scale together so
// `xs` is reasonably scannable for power users with many rows in view, and
// `lg` gives a touch-friendlier hit area for setups with fewer columns.
const DENSITY_STYLES = {
  xs: { padding: '1px 8px', height: 22, fontSize: 11 },
  sm: { padding: '2px 10px', height: 26, fontSize: 12 },
  md: { padding: '4px 10px', height: 30, fontSize: 13 },
  lg: { padding: '8px 12px', height: 38, fontSize: 14 }
}

function getDensity (prefs) {
  return DENSITY_STYLES[prefs?.styling?.density] || DENSITY_STYLES.md
}

const useStyles = makeStyles(() => ({
  root: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  },
  scroll: {
    flex: 1,
    overflow: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    tableLayout: 'fixed'
  },
  thead: {
    position: 'sticky',
    top: 0,
    zIndex: 2
  },
  th: {
    padding: '6px 10px',
    fontSize: 11,
    fontWeight: 'var(--font-weight-semibold, 600)',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    color: 'var(--color-neutral-700, #555)',
    background: 'var(--opal-surface-default, #fff)',
    borderBottom: '2px solid var(--opal-border-default, #ddd)',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    verticalAlign: 'bottom'
  },
  thWrap: {
    whiteSpace: 'normal',
    wordBreak: 'break-word'
  },
  thEllipse: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  thSortable: {
    cursor: 'pointer',
    '&:hover': { color: 'var(--color-neutral-900, #222)' }
  },
  thSticky: {
    position: 'sticky',
    left: 0,
    zIndex: 3,
    background: 'var(--opal-surface-default, #fff)'
  },
  thLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4
  },
  td: {
    padding: '4px 10px',
    height: 30,
    fontWeight: 'var(--font-weight-light, 400)',
    borderBottom: '1px solid var(--opal-border-default, #eee)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  tdSticky: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    background: 'inherit'
  },
  tdNegative: {
    color: 'var(--color-danger-500, #d32f2f)'
  },
  tdPositive: {
    color: 'var(--color-success-700, #2e7d32)'
  },
  tdRight: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums'
  },
  row: {
    // The row's background is set as an inline style on the <tr>, but td
    // cells default to transparent so only the tr's background paints
    // underneath. The hover tint must be opaque (low-alpha rgba lets the
    // sticky Name column show through the next column during horizontal
    // scroll), but light enough that legibility/contrast aren't off.
    '&:hover td': {
      background: 'var(--color-primary-50, #f0f7ff)'
    }
  },
  // Zebra striping is keyed off a data-row index computed during the tbody
  // walk (NOT off DOM `:nth-of-type`). Benchmark rows render as real <tr>s
  // in tbody, and using `:nth-of-type` would let benchmarks shift parity on
  // subsequent data rows (striping would flip every time a parent had an
  // assigned benchmark). The class is only applied to data rows whose index
  // (after filtering placeholder rows) is odd, so the visual stripe stays
  // consistent regardless of how many benchmark siblings are interleaved.
  // The :not(:hover) guard hands off to the hover rule when applicable --
  // the two selectors otherwise have equal specificity.
  zebraEvenRow: {
    '&:not(:hover) td': {
      background: 'var(--color-neutral-50, #fafafa)'
    }
  },
  benchmarkRow: {
    background: 'var(--color-neutral-200, #eaeaea)',
    '& td': {
      background: 'var(--color-neutral-200, #eaeaea)',
      borderTop: 0,
      borderBottom: 0,
      color: 'var(--color-neutral-700, #555)',
      fontStyle: 'italic',
      fontWeight: 'var(--font-weight-light, 400)'
    },
    // Neg/pos coloring on benchmark cells must beat the row's color rule.
    // .benchmarkRow td has specificity (0,2,0); plain .tdNegative is (0,1,0)
    // and would lose. Matching .benchmarkRow td.tdNegative bumps to (0,3,0).
    '& td.$tdNegative': {
      color: 'var(--color-danger-500, #d32f2f)'
    },
    '& td.$tdPositive': {
      color: 'var(--color-success-700, #2e7d32)'
    },
    // Left-edge accent on the first cell so benchmarks stay distinct from
    // group rows at deep grouping levels where DEPTH_BACKGROUNDS[4]/[5]
    // collide with the benchmark background. inset box-shadow rather than
    // border-left to avoid layout shift on the sticky __name td.
    '& td:first-child': {
      boxShadow: 'inset 3px 0 0 0 var(--color-primary-400, #3a83f5)'
    }
  },
  dash: {
    color: 'var(--color-neutral-400, #aaa)'
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    minWidth: 0
  },
  nameCellClickable: {
    cursor: 'pointer'
  },
  expandIcon: {
    flexShrink: 0,
    transition: 'transform 150ms ease'
  },
  expandIconExpanded: {
    transform: 'rotate(90deg)'
  },
  expandSpacer: {
    display: 'inline-block',
    width: 14,
    flexShrink: 0
  },
  nameText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  tfoot: {
    position: 'sticky',
    bottom: 0,
    zIndex: 2
  },
  // Empty-state cell rendered when the row model is empty but a totalRow
  // is present (e.g. a scope filter excluded every account). Uses Opal
  // semantic tokens with fallbacks so the styling stays consistent with
  // the design system rather than hardcoded values.
  emptyMessageCell: {
    padding: 'var(--dim-lg, 24px) var(--dim-sm, 12px)',
    textAlign: 'center',
    color: 'var(--text-color-disabled, #888)',
    fontStyle: 'italic',
    fontSize: 'var(--font-size-sm, 13px)'
  },
  // Small superscript flag on the Total row Name cell when scope filters
  // are active, signaling that the household total is *unfiltered* (a
  // known OAT FilterInGroupingRoutine limitation -- see the comment in
  // nestGroupings). cursor: 'help' cues the user that hovering reveals
  // the explanation via the parent `<span title=...>` tooltip.
  totalCaveat: {
    marginLeft: 'var(--dim-1, 4px)',
    color: 'var(--color-warning-600, #d18800)',
    fontWeight: 600,
    cursor: 'help'
  },
  totalTd: {
    padding: '6px 10px',
    height: 32,
    fontWeight: 'var(--font-weight-bold, 700)',
    // Inset box-shadow renders reliably inside the cell box even when the
    // row is sticky; a plain border-top can paint between scrolling layers
    // and disappear as tbody scrolls under the tfoot.
    boxShadow: 'inset 0 2px 0 0 var(--color-neutral-500, #888)',
    borderTop: 0,
    background: 'var(--opal-surface-default, #fff)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  contextMenuItem: {
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 32
  },
  // Non-clickable header in the context menu showing the full row name.
  // Useful when the visible name is truncated by the column width and the
  // user needs to see the tail (e.g. account number suffixes).
  contextMenuHeader: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.4,
    color: 'var(--color-neutral-800, #333)',
    padding: '8px 16px',
    maxWidth: 360,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    backgroundColor: 'var(--color-neutral-50, #fafafa)'
  }
}))

/**
 * Renders a benchmark `<tr>` outside the TanStack row model. Benchmarks are
 * stored on `row.benchmarks` (and `totalRow.benchmarks`) and rendered manually
 * so they remain pinned immediately after their parent regardless of column
 * sort direction. Cells are matched by column id (which equals the user's
 * column instance id), so any benchmark-eligible material column lights up
 * with its benchmark counterpart value automatically.
 */
function renderBenchmarkRow ({ benchmark, rowKey, depth, columns, classes, formatter, prefs, densityStyle, growthColors }) {
  return (
    <tr key={rowKey} className={classes.benchmarkRow}>
      {columns.map(col => {
        const meta = col.columnDef.meta
        const isName = col.id === '__name'
        const isRight = meta?.align === 'right' || !isName
        const value = isName ? benchmark.name : benchmark[col.id]
        // Neg/pos coloring matches regular rows so a -2% benchmark return is
        // red and a +5% returns-formatted value is green. Specificity-wise,
        // .tdNegative / .tdPositive are JSS classes generated alongside .td
        // and .benchmarkRow; the more specific selector for the cell wins,
        // so explicit colors on the cell override the row's neutral-700.
        const isNegative = growthColors && !isName && typeof value === 'number' && value < 0
        const isPositiveGainStyled = growthColors && !isName && meta?.format === 'returns' && typeof value === 'number' && value > 0
        const fmt = effectiveFormat(meta?.format, meta?.measureKind, prefs)
        return (
          <td
            key={col.id}
            className={clsx(classes.td, {
              [classes.tdSticky]: isName,
              [classes.tdRight]: isRight && !isName,
              [classes.tdNegative]: isNegative,
              [classes.tdPositive]: isPositiveGainStyled
            })}
            style={{ width: col.getSize(), ...densityStyle }}
          >
            {isName
              ? (
                <div className={classes.nameCell} style={{ paddingLeft: depth * 20 + 8 + 14 }}>
                  <span className={classes.nameText} title={typeof value === 'string' ? value : undefined}>{value}</span>
                </div>
              )
              : (value == null
                ? <span className={classes.dash}>--</span>
                : formatter(value, fmt)
              )}
          </td>
        )
      })}
    </tr>
  )
}

function buildColumns (columnDefinitions, formatter, classes, onToggleExpand, prefs) {
  const nameColumn = {
    id: '__name',
    // accessorFn (rather than accessorKey) so sorting follows the displayed
    // name. With nicknames on, sorting by canonical `row.name` would put
    // alphabetically-named accounts in confusingly-different positions from
    // what the user sees.
    accessorFn: row => resolveDisplayName(row, prefs),
    header: 'Name',
    size: 280,
    minSize: 160,
    enableSorting: true,
    cell: ({ row }) => {
      const canExpand = row.getCanExpand()
      const depth = row.depth
      const isFetchingChildren = row.original?._subRowsFetching
      const displayName = resolveDisplayName(row.original, prefs)

      return (
        <div
          className={clsx(classes.nameCell, { [classes.nameCellClickable]: canExpand })}
          style={{ paddingLeft: depth * 20 + 8 }}
          onClick={canExpand ? () => onToggleExpand(row) : undefined}
        >
          {canExpand ? (
            isFetchingChildren
              ? <CircularProgress size={12} style={{ marginRight: 2, flexShrink: 0 }} />
              : (
                <Icon
                  additionalClasses={clsx(classes.expandIcon, { [classes.expandIconExpanded]: row.getIsExpanded() })}
                  name='chevronRight'
                  customSize={14}
                />
              )
          ) : (
            <span className={classes.expandSpacer} />
          )}
          <span className={classes.nameText} title={typeof displayName === 'string' ? displayName : undefined}>{displayName}</span>
        </div>
      )
    }
  }

  const dataColumns = columnDefinitions.map(col => {
    const fmt = effectiveFormat(col.format, col.measureKind, prefs)
    return {
      id: col.accessorKey,
      accessorKey: col.accessorKey,
      header: col.header,
      size: 130,
      minSize: 90,
      enableSorting: true,
      meta: { format: col.format, measureKind: col.measureKind, align: col.align },
      cell: ({ getValue }) => {
        const value = getValue()
        if (value == null) return <span className={classes.dash}>--</span>
        return formatter(value, fmt)
      }
    }
  })

  return [nameColumn, ...dataColumns]
}

export default function PortfolioExplorerGrid ({ rows, columnDefinitions, totalRow, onExpandRow, groupings = [], expandToLevelLazy, expandAllChildrenLazy, lazyExpandState, cancelLazyExpand, viewName, asOfDate, prefs = DEFAULT_PREFERENCES, hasActiveScopeFilters = false }) {
  const classes = useStyles()
  const { formatter } = useFormattingContext()
  const { clientId } = useAppContext()
  const { getLabel: getGroupingLabel } = useGroupingCatalog(clientId)
  const groupingLabels = useMemo(() => groupings.map(getGroupingLabel), [groupings, getGroupingLabel])
  const densityStyle = getDensity(prefs)
  const headerMode = prefs?.styling?.headerMode || 'wrap'
  const zebra = !!prefs?.styling?.zebra
  const growthColors = prefs?.display?.growthColors !== false
  const { downloadCSVFile } = useCSV()
  const [sorting, setSorting] = useState([])
  const [expanded, setExpanded] = useState({})
  const [contextMenu, setContextMenu] = useState(null)
  const [hoveredDepth, setHoveredDepth] = useState(null)
  const [pendingExport, setPendingExport] = useState(null)
  const exportExpandStartedRef = useRef(false)

  const handleExpandRow = useCallback((row) => {
    if (onExpandRow) onExpandRow(row)
  }, [onExpandRow])

  const toggleExpandRow = useCallback((row) => {
    if (!row.getCanExpand()) return
    const isFetchingChildren = row.original?._subRowsFetching
    const needsLazyLoad = row.original?._childrenLoaded === false
    if (isFetchingChildren) return

    const toggle = row.getToggleExpandedHandler()
    if (needsLazyLoad) {
      handleExpandRow(row.original)
      if (!row.getIsExpanded()) {
        if (document.startViewTransition) {
          document.startViewTransition(() => toggle())
        } else {
          toggle()
        }
      }
    } else {
      if (document.startViewTransition) {
        document.startViewTransition(() => toggle())
      } else {
        toggle()
      }
    }
  }, [handleExpandRow])

  const expandAllDescendants = useCallback((row) => {
    const ids = {}
    const walk = (r) => {
      if (r.getCanExpand()) {
        ids[r.id] = true
        if (r.subRows) r.subRows.forEach(walk)
      }
    }
    walk(row)
    setExpanded(prev => ({ ...prev, ...ids }))
  }, [])

  const [pendingExpandAllId, setPendingExpandAllId] = useState(null)

  const collapseAllDescendants = useCallback((row) => {
    const idsToRemove = new Set()
    const walk = (r) => {
      idsToRemove.add(r.id)
      if (r.subRows) r.subRows.forEach(walk)
    }
    if (row.subRows) row.subRows.forEach(walk)
    idsToRemove.add(row.id)
    setExpanded(prev => {
      const next = { ...prev }
      idsToRemove.forEach(id => delete next[id])
      return next
    })
  }, [])

  const handleContextMenu = useCallback((e, row) => {
    e.preventDefault()
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, row })
  }, [])

  const closeContextMenu = useCallback(() => setContextMenu(null), [])

  const columns = useMemo(
    () => buildColumns(columnDefinitions, formatter, classes, toggleExpandRow, prefs),
    [columnDefinitions, formatter, classes, toggleExpandRow, prefs]
  )

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, expanded },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    getSubRows: row => row._placeholder ? undefined : row.data,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel()
  })

  useEffect(() => {
    if (pendingExpandAllId == null) return
    const row = table.getRow(pendingExpandAllId)
    if (row) expandAllDescendants(row)
    setPendingExpandAllId(null)
  }, [pendingExpandAllId, table, expandAllDescendants])

  const syncExpandToLevel = useCallback((targetDepth) => {
    const ids = {}
    for (const row of table.getCoreRowModel().flatRows) {
      if (row.depth < targetDepth && row.getCanExpand()) {
        ids[row.id] = true
      }
    }
    setExpanded(ids)
  }, [table])

  const expandToLevel = useCallback(async (targetDepth) => {
    const hasUnloaded = table.getCoreRowModel().flatRows.some(row =>
      row.depth < targetDepth && row.original?._childrenLoaded === false
    )
    if (hasUnloaded && expandToLevelLazy) {
      const result = await expandToLevelLazy(targetDepth, groupings)
      syncExpandToLevel(result?.targetDepth ?? targetDepth)
    } else {
      syncExpandToLevel(targetDepth)
    }
  }, [table, expandToLevelLazy, groupings, syncExpandToLevel])

  const collapseToLevel = useCallback((targetDepth) => {
    const keep = {}
    for (const row of table.getCoreRowModel().flatRows) {
      if (row.depth < targetDepth && row.getIsExpanded()) {
        keep[row.id] = true
      }
    }
    setExpanded(keep)
  }, [table])

  const executeExport = useCallback((scope, format) => {
    const headers = buildHeaders(columnDefinitions)
    const flatRows = scope === 'visible'
      ? flattenVisibleRows(table.getRowModel().rows, columnDefinitions, formatter, totalRow, prefs)
      : flattenAllRows(rows, columnDefinitions, formatter, totalRow, prefs)

    if (format === 'csv') {
      const csv = buildCSV(flatRows, headers)
      const blob = new Blob(['\uFEFF', csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const namePart = viewName || 'Portfolio Export'
      const datePart = asOfDate ? ` as of ${asOfDate}` : ''
      downloadCSVFile({ url, fileName: `${namePart}${datePart}.csv` })
    } else {
      const tsv = buildTSV(flatRows, headers)
      // TODO: show toast on clipboard failure
      navigator.clipboard.writeText(tsv).catch(() => {})
    }
  }, [columnDefinitions, formatter, totalRow, rows, table, downloadCSVFile, viewName, asOfDate, prefs])

  const handleExport = useCallback(({ scope, format }) => {
    if (scope === 'all' && expandToLevelLazy) {
      const hasUnloaded = table.getCoreRowModel().flatRows.some(
        row => row.original?._childrenLoaded === false
      )
      if (hasUnloaded) {
        exportExpandStartedRef.current = false
        setPendingExport({ format })
        expandToLevelLazy(groupings.length, groupings)
        return
      }
    }
    executeExport(scope, format)
  }, [expandToLevelLazy, table, groupings, executeExport])

  useEffect(() => {
    if (pendingExport && lazyExpandState) {
      exportExpandStartedRef.current = true
    }
    if (pendingExport && !lazyExpandState && exportExpandStartedRef.current) {
      executeExport('all', pendingExport.format)
      setPendingExport(null)
      exportExpandStartedRef.current = false
    }
  }, [pendingExport, lazyExpandState, executeExport])

  return (
    <div className={classes.root}>
      <GroupingBreadcrumb
        groupings={groupingLabels}
        onExpandToLevel={expandToLevel}
        onCollapseToLevel={collapseToLevel}
        highlightedDepth={hoveredDepth}
        onExport={handleExport}
      />
      <div className={classes.scroll}>
        <table className={classes.table}>
          <thead className={classes.thead}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const meta = header.column.columnDef.meta
                  const isRight = meta?.align === 'right' || header.column.id !== '__name'
                  return (
                    <th
                      key={header.id}
                      className={clsx(classes.th, {
                        [classes.thSticky]: header.column.id === '__name',
                        [classes.thSortable]: header.column.getCanSort(),
                        [classes.thWrap]: headerMode === 'wrap',
                        [classes.thEllipse]: headerMode === 'ellipse'
                      })}
                      style={{
                        width: header.column.getSize(),
                        textAlign: isRight && header.column.id !== '__name' ? 'right' : 'left'
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span className={classes.thLabel}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() === 'asc' && <Icon name='up' customSize={12} />}
                        {header.column.getIsSorted() === 'desc' && <Icon name='down' customSize={12} />}
                      </span>
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {/*
              When the row model is empty but a totalRow is present (e.g.
              a scope filter excluded every account at this level), surface
              a single colspan row so the user knows the request returned
              and the grid simply has nothing to display at the row level.
              The totalRow still renders in the tfoot below.

              colSpan tracks visible leaf columns (not getAllColumns) so
              the empty-state row stays aligned with the visible body if
              any columns are hidden via TanStack's column visibility.
              dataRows is computed once and reused by both the empty-state
              check and the body map to avoid re-filtering each render.
            */}
            {(() => {
              const dataRows = table.getRowModel().rows.filter(r => !r.original?._placeholder)
              const visibleColCount = table.getVisibleLeafColumns().length
              return (
                <>
                  {dataRows.length === 0 && totalRow && (
                    <tr>
                      <td colSpan={visibleColCount} className={classes.emptyMessageCell}>
                        No rows match the current filters.
                      </td>
                    </tr>
                  )}
                  {dataRows.flatMap((row, dataIdx) => {
                    // Zebra suppresses the depth-tinted background since alternating
                    // tints would otherwise compete with the depth gradient. The
                    // base must stay opaque (not `transparent`) so the sticky Name
                    // column doesn't let the next column show through during
                    // horizontal scroll. `zebraEvenRow` is keyed off `dataIdx`
                    // (data-row index, post placeholder filter) rather than DOM
                    // `:nth-of-type` so interleaved benchmark <tr>s don't shift
                    // parity on subsequent data rows.
                    const depthBg = zebra ? DEPTH_BACKGROUNDS[0] : DEPTH_BACKGROUNDS[Math.min(row.depth, 5)]
                    const elements = [
                      <tr
                        key={row.id}
                        className={clsx(classes.row, { [classes.zebraEvenRow]: zebra && dataIdx % 2 === 1 })}
                        style={{ background: depthBg }}
                        onContextMenu={(e) => handleContextMenu(e, row)}
                        onMouseEnter={() => setHoveredDepth(row.depth)}
                        onMouseLeave={() => setHoveredDepth(null)}
                      >
                        {row.getVisibleCells().map(cell => {
                          const meta = cell.column.columnDef.meta
                          const isRight = meta?.align === 'right' || cell.column.id !== '__name'
                          const rawValue = cell.getValue()
                          const isNegative = growthColors && typeof rawValue === 'number' && rawValue < 0
                          const isPositiveGainStyled = growthColors && meta?.format === 'returns'
                          return (
                            <td
                              key={cell.id}
                              className={clsx(classes.td, {
                                [classes.tdSticky]: cell.column.id === '__name',
                                [classes.tdRight]: isRight && cell.column.id !== '__name',
                                [classes.tdNegative]: isNegative,
                                [classes.tdPositive]: isPositiveGainStyled && typeof rawValue === 'number' && rawValue > 0
                              })}
                              style={{ width: cell.column.getSize(), ...densityStyle }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          )
                        })}
                      </tr>
                    ]

                    // Benchmarks follow DDPTable sibling-row semantics: visible
                    // whenever the parent row is visible, regardless of the parent's
                    // own expand state. They disappear only when a grandparent is
                    // collapsed (which removes this row from the row model entirely).
                    const benchmarks = row.original?.benchmarks
                    if (benchmarks?.length) {
                      benchmarks.forEach((b, bIdx) => {
                        elements.push(renderBenchmarkRow({
                          benchmark: b,
                          rowKey: `${row.id}-bench-${bIdx}`,
                          depth: row.depth + 1,
                          columns: table.getAllColumns(),
                          classes,
                          formatter,
                          prefs,
                          densityStyle,
                          growthColors
                        }))
                      })
                    }

                    return elements
                  })}
                </>
              )
            })()}
          </tbody>
          {totalRow && (
            <tfoot className={classes.tfoot}>
              <tr>
                {table.getAllColumns().map(col => {
                  const meta = col.columnDef.meta
                  const isName = col.id === '__name'
                  const isRight = meta?.align === 'right' || !isName
                  // Route the displayed total-row label through the shared
                  // resolver for consistency. TotalRow doesn't carry a
                  // nickname today (Household-level aggregate), so this is
                  // currently a no-op -- but if Household nicknames or a
                  // future "use household nicknames" pref ever lands, the
                  // tfoot label updates without another touch.
                  const value = isName ? resolveDisplayName(totalRow, prefs) : totalRow[col.id]
                  const fmt = effectiveFormat(meta?.format, meta?.measureKind, prefs)
                  return (
                    <td
                      key={col.id}
                      className={clsx(classes.totalTd, {
                        [classes.tdSticky]: isName,
                        [classes.tdRight]: isRight && !isName,
                        [classes.tdNegative]: growthColors && typeof value === 'number' && value < 0
                      })}
                      style={{ width: col.getSize() }}
                    >
                      {isName
                        ? (hasActiveScopeFilters
                          ? (
                            <span
                              style={{ paddingLeft: 8 }}
                              title='Total reflects unfiltered household values'
                            >
                              {value}
                              <sup className={classes.totalCaveat}>*</sup>
                            </span>
                          )
                          : <span style={{ paddingLeft: 8 }}>{value}</span>
                        )
                        : (value == null
                          ? <span className={classes.dash}>--</span>
                          : formatter(value, fmt)
                        )}
                    </td>
                  )
                })}
              </tr>
              {totalRow.benchmarks?.map((b, bIdx) => renderBenchmarkRow({
                benchmark: b,
                rowKey: `total-bench-${bIdx}`,
                depth: 0,
                columns: table.getAllColumns(),
                classes,
                formatter,
                prefs,
                densityStyle,
                growthColors
              }))}
            </tfoot>
          )}
        </table>
      </div>
      <Menu
        open={contextMenu !== null}
        onClose={closeContextMenu}
        anchorReference='anchorPosition'
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        {contextMenu?.row.original?.name && (
          <ListSubheader disableSticky className={classes.contextMenuHeader}>
            {resolveDisplayName(contextMenu.row.original, prefs)}
          </ListSubheader>
        )}
        {contextMenu?.row.original?.name && <Divider />}
        {contextMenu?.row.getCanExpand() ? [
          <MenuItem
            key='toggle'
            onClick={() => { toggleExpandRow(contextMenu.row); closeContextMenu() }}
          >
            <span className={classes.contextMenuItem}>
              <Icon name={contextMenu.row.getIsExpanded() ? 'collapse' : 'expand'} customSize={14} />
              {contextMenu.row.getIsExpanded() ? 'Collapse' : 'Expand'}
            </span>
          </MenuItem>,
          <MenuItem
            key='expand-all'
            onClick={async () => {
              const row = contextMenu.row
              closeContextMenu()
              if (expandAllChildrenLazy) {
                await expandAllChildrenLazy(row.original)
                setPendingExpandAllId(row.id)
              } else {
                expandAllDescendants(row)
              }
            }}
          >
            <span className={classes.contextMenuItem}>
              <Icon name='unfoldMore' customSize={14} />
              Expand All Children
            </span>
          </MenuItem>,
          <MenuItem
            key='collapse-all'
            disabled={!contextMenu.row.getIsExpanded()}
            onClick={() => { collapseAllDescendants(contextMenu.row); closeContextMenu() }}
          >
            <span className={classes.contextMenuItem}>
              <Icon name='unfoldLess' customSize={14} />
              Collapse All Children
            </span>
          </MenuItem>
        ] : (
          <MenuItem disabled>
            <span className={classes.contextMenuItem}>No actions available</span>
          </MenuItem>
        )}
      </Menu>
      <LazyExpandDialog
        open={!!lazyExpandState}
        state={lazyExpandState}
        onCancel={cancelLazyExpand || (() => {})}
      />
    </div>
  )
}

PortfolioExplorerGrid.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  columnDefinitions: PropTypes.arrayOf(PropTypes.shape({
    accessorKey: PropTypes.string,
    header: PropTypes.string,
    format: PropTypes.string,
    measureKind: PropTypes.string,
    align: PropTypes.string
  })).isRequired,
  totalRow: PropTypes.object,
  onExpandRow: PropTypes.func,
  groupings: PropTypes.arrayOf(PropTypes.string),
  expandToLevelLazy: PropTypes.func,
  expandAllChildrenLazy: PropTypes.func,
  lazyExpandState: PropTypes.object,
  cancelLazyExpand: PropTypes.func,
  viewName: PropTypes.string,
  asOfDate: PropTypes.string,
  prefs: PropTypes.shape({
    styling: PropTypes.object,
    display: PropTypes.object
  }),
  hasActiveScopeFilters: PropTypes.bool
}
